import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { MergeRepresentationType } from '@readme/api/src/mappings/merge/types';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import MergeBranchModal from '.';

interface MergeBranchModalContextValue {
  /**
   * Indicates whether a branch is preparing to be merged or not.
   */
  branchToMerge: BranchRepresentationType | null;

  /**
   * Merge status about `branchToMerge` when set. Is `null` otherwise.
   */
  mergeStatus: MergeRepresentationType | null;

  /**
   * Configures the merge branch modal by assigning it a `branch` along with a
   * branch merge `status` object that determines whether to perform a force
   * merge or not. Setting `branch` to `null` closes the modal.
   * @example
   * setBranchToMerge(branch, status)
   * setBranchToMerge(null)
   */
  setBranchToMerge: <Branch extends MergeBranchModalContextValue['branchToMerge']>(
    branch: Branch,
    status?: Branch extends null ? never : MergeBranchModalContextValue['mergeStatus'],
  ) => void;
}

const MergeBranchModalContext = createContext<MergeBranchModalContextValue | null>(null);
MergeBranchModalContext.displayName = 'MergeBranchModalContext';

export const MergeBranchModalProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [branchToMerge, setBranchToMerge] = useState<MergeBranchModalContextValue['branchToMerge']>(null);
  const [mergeStatus, setMergeStatus] = useState<MergeBranchModalContextValue['mergeStatus']>(null);

  const externalSetBranchToMerge = useCallback<MergeBranchModalContextValue['setBranchToMerge']>((branch, status) => {
    setBranchToMerge(branch);
    setMergeStatus(status || null);
  }, []);

  return (
    <MergeBranchModalContext.Provider
      value={useMemo(
        () => ({
          branchToMerge,
          mergeStatus,
          setBranchToMerge: externalSetBranchToMerge,
        }),
        [mergeStatus, branchToMerge, externalSetBranchToMerge],
      )}
    >
      {children}
      <MergeBranchModal />
    </MergeBranchModalContext.Provider>
  );
};

/**
 * Returns state that controls whether `<MergeBranchModal>` is open.
 */
export const useMergeBranchModalContext = () => {
  const value = useContext(MergeBranchModalContext);
  if (!value) throw new Error('MergeBranchModalProvider is missing');
  return value;
};
